import { useMemo } from 'react'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useToggle from 'hooks/useToggle'
import AccommodationPackageInclusionsList from './AccommodationPackageInclusionsList'
import { getVisiblePackageInclusionsCount, type PackageInclusionCategory, type PackageInclusionsDisclosure, type PackageInclusionGroup } from './packageInclusions'

export type { PackageInclusionGroup, PackageInclusionRow } from './packageInclusions'

const categoryOrder: Array<PackageInclusionCategory> = ['lux-plus', 'exclusive', 'night-extra', 'standard']
const mobileInclusionLimit = 4

interface Props {
  groups: Array<PackageInclusionGroup>
  limit: 4 | 8
  showAll?: boolean
  disclosure?: PackageInclusionsDisclosure
}

/** Eligible content comes from each product. Only ordering, appearance and disclosure live here. */
function AccommodationPackageInclusions({ groups, limit, showAll = false, disclosure }: Props) {
  const { value: localExpanded, toggle: toggleLocalExpanded } = useToggle()
  const expanded = disclosure?.expanded ?? localExpanded
  const onToggle = disclosure?.onToggle ?? toggleLocalExpanded
  const entries = useMemo(() => categoryOrder.flatMap(category => groups
    .filter(group => group.category === category)
    .flatMap(group => group.rows.map((row, index) => ({
      category,
      row,
      heading: index === 0 ? group.heading : undefined,
    })))), [groups])
  const collapsedMobileCount = getVisiblePackageInclusionsCount(entries.length, { limit: mobileInclusionLimit, showAll })
  const collapsedWideCount = getVisiblePackageInclusionsCount(entries.length, { limit, showAll })
  const splitByBreakpoint = !expanded && collapsedMobileCount !== collapsedWideCount

  if (!entries.length) return null

  if (!splitByBreakpoint) {
    return <AccommodationPackageInclusionsList
      entries={entries}
      limit={limit}
      showAll={showAll}
      expanded={expanded}
      onToggle={onToggle}
    />
  }

  return <>
    <CSSBreakpoint max="mobile">
      <AccommodationPackageInclusionsList
        entries={entries}
        limit={mobileInclusionLimit}
        showAll={showAll}
        expanded={expanded}
        onToggle={onToggle}
      />
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <AccommodationPackageInclusionsList
        entries={entries}
        limit={limit}
        showAll={showAll}
        expanded={expanded}
        onToggle={onToggle}
      />
    </CSSBreakpoint>
  </>
}

export default AccommodationPackageInclusions
